import Vue from 'vue'

/**
 * Shared state for the mandatory profile-details gate.
 *
 * Three places feed it and one place reads it:
 *
 *   feed  · main.js and Login.vue, from the /api/auth/me payload
 *   feed  · the axios interceptor, when any write is refused 403
 *           PROFILE_INCOMPLETE (a user who cleared a required field, or
 *           whose stored verdict went stale mid-session)
 *   feed  · ProfileCompletionModal / ProfileSettings, from /api/profile
 *   read  · ProfileCompletionModal, mounted once in TheContainer
 *
 * `known` keeps the modal from flashing on first paint: until a server
 * verdict has actually arrived we assume nothing and show nothing.
 */

const DEFER_KEY = 'profile_gate_deferred'

const state = Vue.observable({
  known: false,
  complete: true,
  enforced: true,
  // While `mandatory` is false the gate is a dismissible reminder and the
  // server still accepts writes. It flips once the warnings run out.
  mandatory: false,
  warningsAllowed: 0,
  warningsUsed: 0,
  warningsRemaining: 0,
  // Set when the user picks "Remind me later". Session-scoped, so reloading
  // the tab doesn't nag again (or burn another warning); a fresh login does.
  deferred: readDeferred(),
  required: [],
  labels: {},
  missing: [],
})

function readDeferred () {
  try {
    return sessionStorage.getItem(DEFER_KEY) === '1'
  } catch (error) {
    return false
  }
}

function writeDeferred (value) {
  try {
    if (value) sessionStorage.setItem(DEFER_KEY, '1')
    else sessionStorage.removeItem(DEFER_KEY)
  } catch (error) {
    // Private-mode storage failures just mean the reminder shows again.
  }
}

function normalise (payload) {
  if (!payload || typeof payload !== 'object') return null

  const toCount = (value) => (Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0)

  return {
    complete: payload.complete !== false,
    enforced: payload.enforced !== false,
    mandatory: payload.mandatory === true,
    warningsAllowed: toCount(payload.warnings_allowed),
    warningsUsed: toCount(payload.warnings_used),
    warningsRemaining: toCount(payload.warnings_remaining),
    required: Array.isArray(payload.required) ? payload.required : [],
    labels: payload.labels && typeof payload.labels === 'object' ? payload.labels : {},
    missing: Array.isArray(payload.missing) ? payload.missing : [],
  }
}

/** Apply a `profile_completion` block from any endpoint that carries one. */
export function applyCompletion (payload) {
  const next = normalise(payload)
  if (!next) return false

  state.complete = next.complete
  state.enforced = next.enforced
  state.mandatory = next.mandatory
  state.warningsAllowed = next.warningsAllowed
  state.warningsUsed = next.warningsUsed
  state.warningsRemaining = next.warningsRemaining
  state.required = next.required
  state.labels = next.labels
  state.missing = next.missing
  state.known = true

  // A completed profile clears any deferral, so a later regression (someone
  // blanks a field) shows the reminder again instead of staying silent.
  if (next.complete) deferGate(false)

  return true
}

/** "Remind me later" — hide the reminder for the rest of this tab session. */
export function deferGate (value = true) {
  state.deferred = !!value
  writeDeferred(!!value)
}

/** Pull the block out of an /api/auth/me (or login) user payload. */
export function applyAuthUser (user) {
  return user ? applyCompletion(user.profile_completion) : false
}

/**
 * Re-read the verdict cached in localStorage by main.js. Lets the gate
 * appear on the very first paint after a reload instead of waiting on the
 * /auth/me round trip.
 */
export function hydrateFromStorage () {
  try {
    const raw = localStorage.getItem('auth_user')
    return raw ? applyAuthUser(JSON.parse(raw)) : false
  } catch (error) {
    return false
  }
}

/**
 * Called by the 403 PROFILE_INCOMPLETE interceptor. The response carries a
 * fresh completion block; fall back to a bare "incomplete" if it doesn't so
 * the gate still opens and can load the details itself.
 */
export function markIncomplete (payload) {
  if (!applyCompletion(payload)) {
    state.complete = false
    state.enforced = true
    state.mandatory = true
    state.warningsRemaining = 0
    state.known = true
  }

  // The server only refuses writes once the allowance is spent, so a 403
  // always means the gate is now a wall — an earlier deferral is void.
  deferGate(false)
}

/** Wipe on logout so the next user starts from their own verdict. */
export function resetCompletion () {
  state.known = false
  state.complete = true
  state.enforced = true
  state.mandatory = false
  state.warningsAllowed = 0
  state.warningsUsed = 0
  state.warningsRemaining = 0
  state.required = []
  state.labels = {}
  state.missing = []
  deferGate(false)
}

export function isGateOpen () {
  if (!state.known || state.complete || state.enforced === false) return false
  return state.mandatory || !state.deferred
}

export default {
  state,
  applyCompletion,
  applyAuthUser,
  hydrateFromStorage,
  markIncomplete,
  deferGate,
  resetCompletion,
  isGateOpen,
}
